import { AdminNav } from '@/components/admin-nav'
import { BatchSection } from '@/components/batch-section'
import { BlockListSection } from '@/components/block-list-section'
import { CacheSection } from '@/components/cache-section'
import { ControlPanel } from '@/components/control-panel'
import { AdminDirectMessagesSection } from '@/components/admin-direct-messages-section'
import { EmailServiceSection } from '@/components/email-service-section'
import { EmailWaveReport } from '@/components/email-wave-report'
import { LocalizedAnnouncementSection } from '@/components/localized-announcement-section'
import { UserDashboardSection } from '@/components/user-dashboard-section'
import { ModelCatalogSection } from '@/components/model-catalog-section'
import { AdminAccessGate } from '@/components/admin-access-gate'
import { QuerySection } from '@/components/query-section'
import { PromptInspectorSection } from '@/components/prompt-inspector-section'
import { ReconcileSection } from '@/components/reconcile-section'
import { ReportsSection } from '@/components/reports-section'
import { StaticsSection } from '@/components/statics-section'
import { ThemeProvider } from '@/components/theme-provider'
import { TopupSection } from '@/components/topup-section'
import { PromoCodesSection } from '@/components/promo-codes-section'
import { TournamentSection } from '@/components/tournament-section'
import { Button } from '@/components/ui/button'
import { useI18n } from '@/lib/i18n'
import { StimulateGamesBatchResponse, trpcClient } from '@/lib/trpc'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'

type GameSettingType = string

type StimulationRequest = {
  clientRequestId: string
  gameCount: number
  languagePercentages: Record<string, number>
  gameSetting: string
  goodModels: string[]
  evilModels: string[]
}

const PENDING_STIMULATION_KEY = 'mentiss.admin.pendingStimulation'
const MAX_BATCH_GAMES = 10_000

function readPendingStimulation(): StimulationRequest | null {
  try {
    const stored = localStorage.getItem(PENDING_STIMULATION_KEY)
    if (!stored)
      return null
    const value: unknown = JSON.parse(stored)
    if (typeof value !== 'object' || value === null)
      return null
    const request = value as Partial<StimulationRequest>
    if (
      typeof request.clientRequestId !== 'string'
      || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(request.clientRequestId)
      || typeof request.gameCount !== 'number'
      || typeof request.gameSetting !== 'string'
      || !request.languagePercentages
      || typeof request.languagePercentages !== 'object'
      || !Array.isArray(request.goodModels)
      || !Array.isArray(request.evilModels)
    ) {
      return null
    }
    return request as StimulationRequest
  }
  catch {
    return null
  }
}

function savePendingStimulation(request: StimulationRequest | null): boolean {
  try {
    if (request)
      localStorage.setItem(PENDING_STIMULATION_KEY, JSON.stringify(request))
    else
      localStorage.removeItem(PENDING_STIMULATION_KEY)
    return true
  }
  catch {
    return false
  }
}

function getBatchGameCount(
  gameCount: number,
  goodModels: string[],
  evilModels: string[],
) {
  if (!Number.isSafeInteger(gameCount) || gameCount < 1 || gameCount > 500)
    return null

  let validCombinations = 0
  let excludedCombinations = 0
  for (const goodModel of goodModels) {
    for (const evilModel of evilModels) {
      if (goodModel !== evilModel)
        validCombinations++
      else
        excludedCombinations++
    }
  }

  const totalGames = validCombinations * gameCount
  if (
    validCombinations === 0
    || !Number.isSafeInteger(validCombinations)
    || !Number.isSafeInteger(excludedCombinations)
    || !Number.isSafeInteger(totalGames)
  ) {
    return null
  }

  return { validCombinations, excludedCombinations, totalGames }
}

function App() {
  const [pendingStimulation, setPendingStimulation] = useState<StimulationRequest | null>(
    readPendingStimulation,
  )
  const [isLoading, setIsLoading] = useState(false)
  const [response, setResponse] = useState('')
  const [isComplete, setIsComplete] = useState(false)
  const [gameCount, setGameCount] = useState(pendingStimulation?.gameCount ?? 1)
  const [gameSetting, setGameSetting] = useState<GameSettingType>(
    pendingStimulation?.gameSetting ?? 'D10_ULTIMATE_TRIAL',
  )
  const [goodModels, setGoodModels] = useState<string[]>(pendingStimulation?.goodModels ?? [])
  const [evilModels, setEvilModels] = useState<string[]>(pendingStimulation?.evilModels ?? [])
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    Object.keys(pendingStimulation?.languagePercentages ?? {})[0] ?? 'zh',
  )
  const [showStimulationConfirm, setShowStimulationConfirm] = useState(false)
  const { t } = useI18n()
  const batchGameCount = getBatchGameCount(gameCount, goodModels, evilModels)

  const handleStimulateGame = async () => {
    if (!batchGameCount || batchGameCount.totalGames > MAX_BATCH_GAMES)
      return

    setIsLoading(true)
    setResponse('')
    setIsComplete(false)

    try {
      const singleLanguagePercentages = { [selectedLanguage]: 100 }
      const settings = {
        gameCount,
        languagePercentages: singleLanguagePercentages,
        gameSetting,
        goodModels,
        evilModels,
      }
      const previousSettings = pendingStimulation && {
        gameCount: pendingStimulation.gameCount,
        languagePercentages: pendingStimulation.languagePercentages,
        gameSetting: pendingStimulation.gameSetting,
        goodModels: pendingStimulation.goodModels,
        evilModels: pendingStimulation.evilModels,
      }
      const request = previousSettings
        && JSON.stringify(previousSettings) === JSON.stringify(settings)
        ? pendingStimulation
        : { ...settings, clientRequestId: crypto.randomUUID() }
      setPendingStimulation(request)
      savePendingStimulation(request)

      const result: StimulateGamesBatchResponse
        = await trpcClient.batchRouter.stimulateGamesBatch.mutate(request)

      if (result.pendingCount === 0) {
        setPendingStimulation(null)
        savePendingStimulation(null)
      }

      const gameIdLimitCue = result.omittedGameIds > 0
        ? ` ${t('admin.response.gameIdsTruncated')
            .replace('{shown}', String(result.gameIds.length))
            .replace('{total}', String(result.gameCount))}`
        : ''

      setResponse(
        `${result.success ? `✅ ${t('admin.response.success')}` : '⚠️'} ${result.message}${gameIdLimitCue}\n\n`
        + `${t('admin.response.gameIds')}: ${result.gameIds.join(', ')}\n`
        + `${t('admin.response.totalGames')}: ${result.gameCount}\n`
        + `Model combinations: ${result.combinations}\n`
        + `Selected models: ${goodModels.join(', ')} (good) × ${evilModels.join(', ')} (evil)\n\n`
        + `Language Distribution:\n`
        + Object.entries(result.languageStats)
          .map(
            ([lang, stats]) =>
                `  ${lang}: ${stats.gamesCreated} games (${stats.gameIds.join(', ')})`,
          )
          .join('\n'),
      )
      setIsComplete(true)
    }
    catch (error) {
      console.error('Failed to stimulate game:', error)
      setResponse(
        `❌ ${t('admin.response.error')}: ${error instanceof Error ? error.message : t('admin.response.unknownError')}`,
      )
      setIsComplete(true)
    }
    finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    setResponse('')
    setIsComplete(false)
  }

  const handleShowStimulationConfirm = () => {
    if (!batchGameCount || batchGameCount.totalGames > MAX_BATCH_GAMES)
      return

    setShowStimulationConfirm(true)
  }

  const handleConfirmStimulation = async () => {
    if (!batchGameCount || batchGameCount.totalGames > MAX_BATCH_GAMES)
      return

    setShowStimulationConfirm(false)
    await handleStimulateGame()
  }

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <AdminAccessGate>
        <div className="min-h-screen bg-background text-foreground">
          <AdminNav />
          <main className="pt-16">

            <section id="model-catalog">
              <ModelCatalogSection />
            </section>
            <section id="control-panel">
              <ControlPanel />
            </section>
            <section id="statics">
              <StaticsSection />
            </section>

            <section id="stimulation">
              <BatchSection
                response={response}
                isLoading={isLoading}
                isComplete={isComplete}
                onReset={handleReset}
                onStimulateGame={handleShowStimulationConfirm}
                gameCount={gameCount}
                onGameCountChange={setGameCount}
                selectedLanguage={selectedLanguage}
                onLanguageChange={setSelectedLanguage}
                gameSetting={gameSetting}
                onGameSettingChange={setGameSetting}
                goodModels={goodModels}
                onGoodModelsChange={setGoodModels}
                evilModels={evilModels}
                onEvilModelsChange={setEvilModels}
                batchGameCount={batchGameCount}
                maxBatchGames={MAX_BATCH_GAMES}
              />
            </section>

            <section id="tournaments" className="scroll-mt-20">
              <TournamentSection />
            </section>

            <section id="prompt-inspector" className="scroll-mt-20">
              <PromptInspectorSection />
            </section>

            <section id="email-service">
              <EmailServiceSection />
              <div className="mt-6">
                <EmailWaveReport />
              </div>
            </section>

            <section id="direct-messages" className="scroll-mt-20">
              <AdminDirectMessagesSection />
            </section>

            <section id="localized-announcement" className="scroll-mt-20">
              <LocalizedAnnouncementSection />
            </section>

            <section id="query">
              <QuerySection />
            </section>

            <section id="block-list">
              <BlockListSection />
            </section>

            <section id="reports" className="scroll-mt-20">
              <ReportsSection />
            </section>

            <section id="topup">
              <TopupSection />
            </section>

            <section id="promo-codes" className="scroll-mt-20">
              <PromoCodesSection />
            </section>

            <section id="reconcile">
              <ReconcileSection />
            </section>

            <section id="user-dashboard">
              <UserDashboardSection />
            </section>

            <section id="cache">
              <CacheSection />
            </section>
          </main>

          { }
          {showStimulationConfirm && batchGameCount && batchGameCount.totalGames <= MAX_BATCH_GAMES && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
              <div className="mx-4 w-full max-w-lg rounded-lg border bg-background p-6">
                <h3 className="mb-4 text-lg font-semibold">
                  {t('admin.stimulation.confirmTitle')}
                </h3>

                <div className="mb-6 space-y-4">
                  <div className="rounded-lg bg-muted/30 p-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">
                          {t('admin.config.gameCount')}:
                        </span>
                        <span className="ml-2 font-semibold">{gameCount}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">
                          {t('admin.config.totalGames')}:
                        </span>
                        <span className="ml-2 font-semibold">
                          {gameCount} × {batchGameCount.validCombinations} = {batchGameCount.totalGames}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">
                          {t('admin.config.gameSetting')}:
                        </span>
                        <span className="ml-2 font-semibold">
                          {gameSetting}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">
                          {t('admin.config.goodModel')}:
                        </span>
                        <span className="ml-2 font-semibold">{goodModels.join(', ')}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">
                          {t('admin.config.evilModel')}:
                        </span>
                        <span className="ml-2 font-semibold">{evilModels.join(', ')}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-muted-foreground">
                          {t('admin.config.language')}:
                        </span>
                        <span className="ml-2 font-semibold">
                          {selectedLanguage === 'zh' && '🇨🇳 简体中文'}
                          {selectedLanguage === 'en' && '🇺🇸 English'}
                          {selectedLanguage === 'fr' && '🇫🇷 Français'}
                          {selectedLanguage === 'ja' && '🇯🇵 日本語'}
                          {selectedLanguage === 'tw' && '🇭🇰 繁體中文'}
                          {selectedLanguage === 'es' && '🇪🇸 Español'}
                          {selectedLanguage === 'ar' && '🇸🇦 العربية'}
                          {selectedLanguage === 'ko' && '🇰🇷 한국어'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowStimulationConfirm(false)}
                    disabled={isLoading}
                  >
                    {t('admin.stimulation.cancel')}
                  </Button>
                  <Button
                    onClick={handleConfirmStimulation}
                    disabled={isLoading || !batchGameCount}
                    className="flex items-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="size-4 animate-spin" />
                        {t('admin.loading')}
                      </>
                    ) : (
                      t('admin.stimulation.confirm')
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

        </div>
      </AdminAccessGate>
    </ThemeProvider>
  )
}

export default App
